import React, { Component } from "react";
import { Link } from "react-router-dom";
import "./TermsPage.scss";

interface TermsPageProps {}

interface TermsPageState {}

class TermsPage extends Component<TermsPageProps, TermsPageState> {
  constructor(props: TermsPageProps) {
    super(props);
    this.state = {};
  }

  render(): React.JSX.Element {
    return (
      <div className="terms-page">
        <div className="terms-page-container">
          <div className="terms-page-content">
            <h1 className="terms-page-content-title">Terms and Conditions</h1>

            <p>
              <strong>Effective Date:</strong> 5 Apr, 2026
            </p>
            <p>
              <strong>Last Updated:</strong> 9 Apr, 2026
            </p>

            <p>
              Welcome to <strong>EmailAjent.com</strong> (&quot;EmailAjent&quot;, &quot;we&quot;,
              &quot;our&quot;, or &quot;us&quot;). By accessing or using our web application, you
              agree to be bound by the following terms and conditions (&quot;Terms&quot;). If you do
              not agree with these Terms, please do not use the Service.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">1. Service Description</h2>

            <p>
              EmailAjent is a productivity tool that connects with your Google Calendar and allows
              you to manage your schedule via AI-driven natural language interactions.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">2. Eligibility</h2>

            <p>
              By using EmailAjent, you represent that you are at least 18 years of age and capable of
              entering into a legally binding agreement.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">3. User Responsibilities</h2>

            <p>You agree to:</p>

            <ul className="terms-page-list">
              <li>Provide accurate and complete information when registering</li>
              <li>Not use EmailAjent for any illegal or unauthorized purpose</li>
              <li>Be solely responsible for all activity under your account</li>
            </ul>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">4. Google Calendar Integration</h2>

            <p>
              To function, EmailAjent requires full access to your Google Calendar. You authorize us
              to access and interact with your calendar via Google Calendar API to perform actions on
              your behalf.
            </p>

            <p>
              Use of your Google data is governed by our{" "}
              <Link className="terms-page-link" to="/privacy">
                Privacy Policy
              </Link>{" "}
              and Google&apos;s{" "}
              <a
                className="terms-page-link"
                href="https://developers.google.com/terms/api-services-user-data-policy"
                target="_blank"
                rel="noopener noreferrer"
              >
                User Data Policy
              </a>
              .
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">5. OpenAI API Integration</h2>

            <p>
              EmailAjent uses the OpenAI API to power AI-driven features, including natural language
              understanding and responses related to your calendar. By using the Service, you
              authorize us to send relevant requests and data to OpenAI for processing on your
              behalf.
            </p>

            <p>
              Processing of data through these services is described in our{" "}
              <Link className="terms-page-link" to="/privacy">
                Privacy Policy
              </Link>
              . OpenAI&apos;s policies also apply, including their{" "}
              <a
                className="terms-page-link"
                href="https://openai.com/en-GB/policies/row-privacy-policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                className="terms-page-link"
                href="https://openai.com/policies/terms-of-use/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Terms of Use
              </a>
              .
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">6. Data Use and Storage</h2>

            <p>
              We store limited personal data (name, email, calendar data) on secure servers to
              operate the service. We do not sell or share your data for advertising purposes.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">7. Intellectual Property</h2>

            <p>
              All content, trademarks, and software on EmailAjent are the property of EmailAjent.com
              or its licensors. You agree not to copy, distribute, or reverse-engineer any part of
              the service without our written permission.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">8. Termination</h2>

            <p>
              We reserve the right to suspend or terminate your access to EmailAjent at any time,
              without notice or liability, if we believe you have violated these Terms or abused the
              service.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">9. Disclaimers</h2>

            <p>
              EmailAjent is provided &quot;as is&quot; and &quot;as available&quot; without warranties
              of any kind, either express or implied, including but not limited to merchantability,
              fitness for a particular purpose, or non-infringement.
            </p>

            <p>We do not guarantee:</p>

            <ul className="terms-page-list">
              <li>That EmailAjent will always be available or error-free</li>
              <li>That calendar changes will be made correctly in all edge cases</li>
              <li>That your data will never be lost or corrupted</li>
            </ul>

            <p>Use at your own risk.</p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">10. Limitation of Liability</h2>

            <p>
              To the maximum extent permitted by law, in no event shall EmailAjent.com, its creators,
              or affiliates be liable for any indirect, incidental, consequential, or punitive
              damages, or any loss of data, business, or profits arising out of your use or
              inability to use the service.
            </p>

            <p>
              Our total liability shall not exceed the amount you paid us (if any) for using the
              service.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">11. Changes to Terms</h2>

            <p>
              We may update these Terms from time to time. Continued use of the service after such
              updates constitutes your acceptance of the new Terms.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">12. Governing Law</h2>

            <p>
              These Terms are governed by and construed in accordance with the laws of Bangalore,
              Karnataka, India, without regard to its conflict of law principles.
            </p>

            <hr className="terms-page-divider" />

            <h2 className="terms-page-section-title">13. Contact</h2>

            <p>For any questions regarding these Terms, please contact:</p>

            <p>
              <strong>Email:</strong> contact at hq.emailajent.com.
              <br />
              <strong>Website:</strong> https://www.emailajent.com
            </p>
          </div>
        </div>
      </div>
    );
  }
}

export default TermsPage;
